import React, { useState, useEffect } from 'react';
import { API_URL } from '../../utils/constants';

function MaterialOutgoingModal({ material, token, onClose }) {
  const [outgoingList, setOutgoingList] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    fetchOutgoingHistory();
  }, [material.id]);

  const fetchOutgoingHistory = async () => {
    try {
      const response = await fetch(`${API_URL}/materials/outgoing/${material.id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setOutgoingList(data);
      }
    } catch (err) {
      console.error('출고 내역 조회 실패:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString('ko-KR', { 
      year: '2-digit', 
      month: '2-digit', 
      day: '2-digit' 
    });
  };

  const formatCurrency = (value) => {
    if (!value) return '-';
    return Number(value).toLocaleString() + '원';
  };

  const remaining = material.remaining ?? material.quantity;
  const totalOut = material.quantity - remaining;

  return (
    <div style={styles.overlay} onClick={onClose}>
      <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
        {/* 헤더 */}
        <div style={styles.header}>
          <div>
            <h3 style={styles.title}>📦 출고 내역</h3>
            <p style={styles.materialId}>{material.id}</p>
          </div>
          <button onClick={onClose} style={styles.closeBtn}>×</button>
        </div>

        {/* 자재 정보 요약 */}
        <div style={styles.summary}>
          <div style={styles.summaryRow}>
            <span style={styles.summaryLabel}>제품명</span>
            <span style={styles.summaryValue}>{material.product_name}</span>
          </div>
          <div style={styles.summaryRow}>
            <span style={styles.summaryLabel}>항목</span>
            <span style={styles.summaryValue}>{material.category}</span>
          </div>
          <div style={styles.summaryStats}>
            <div style={styles.statBox}>
              <span style={styles.statLabel}>입고량</span>
              <span style={styles.statValue}>{material.quantity}개</span>
            </div>
            <div style={styles.statBox}>
              <span style={styles.statLabel}>출고량</span>
              <span style={{ ...styles.statValue, color: '#dc3545' }}>{totalOut}개</span>
            </div>
            <div style={styles.statBox}>
              <span style={styles.statLabel}>잔여</span>
              <span style={{ ...styles.statValue, color: '#007bff' }}>{remaining}개</span>
            </div>
          </div>
        </div>

        {/* 출고 내역 목록 */}
        <div style={styles.content}>
          {isLoading ? (
            <div style={styles.loading}>로딩 중...</div>
          ) : outgoingList.length === 0 ? (
            <div style={styles.empty}>
              <span style={styles.emptyIcon}>📭</span>
              <p>출고 내역이 없습니다.</p>
            </div>
          ) : (
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>출고일</th>
                  <th style={{ ...styles.th, textAlign: 'center' }}>수량</th>
                  <th style={{ ...styles.th, textAlign: 'right' }}>단가</th>
                  <th style={styles.th}>작업지시서</th>
                  <th style={styles.th}>고객명</th>
                </tr>
              </thead>
              <tbody>
                {outgoingList.map((item, idx) => (
                  <tr key={idx} style={styles.tr}>
                    <td style={styles.td}>{formatDate(item.out_date)}</td>
                    <td style={{ ...styles.td, textAlign: 'center', fontWeight: '600' }}>
                      {item.quantity}개
                    </td>
                    <td style={{ ...styles.td, textAlign: 'right' }}>
                      {formatCurrency(item.unit_price)}
                    </td>
                    <td style={styles.td}>
                      <span style={styles.recordId}>#{item.record_id}</span>
                    </td>
                    <td style={styles.td}>{item.company_name || '-'}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr style={styles.totalRow}>
                  <td style={styles.totalTd}>합계</td>
                  <td style={{ ...styles.totalTd, textAlign: 'center', fontWeight: '700' }}>
                    {outgoingList.reduce((sum, item) => sum + item.quantity, 0)}개
                  </td>
                  <td colSpan={3} style={styles.totalTd}></td>
                </tr>
              </tfoot>
            </table>
          )}
        </div>

        {/* 푸터 */}
        <div style={styles.footer}>
          <button onClick={onClose} style={styles.closeButton}>
            닫기
          </button>
        </div>
      </div>
    </div>
  );
}

const styles = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
    animation: 'fadeIn 0.2s ease'
  },
  modal: {
    backgroundColor: '#fff',
    borderRadius: '12px',
    width: '90%',
    maxWidth: '600px',
    maxHeight: '80vh',
    display: 'flex',
    flexDirection: 'column',
    boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
    animation: 'slideUp 0.3s ease'
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    padding: '20px 24px',
    borderBottom: '1px solid #e8ecef',
    backgroundColor: '#f8f9fa',
    borderRadius: '12px 12px 0 0'
  },
  title: {
    margin: 0,
    fontSize: '18px',
    fontWeight: '700',
    color: '#2c3e50'
  },
  materialId: {
    margin: '4px 0 0 0',
    fontSize: '13px',
    color: '#7f8c8d',
    fontFamily: 'monospace'
  },
  closeBtn: {
    background: 'none',
    border: 'none',
    fontSize: '28px',
    color: '#95a5a6',
    cursor: 'pointer',
    padding: '0',
    lineHeight: '1'
  },
  summary: {
    padding: '16px 24px',
    backgroundColor: '#f8f9fa',
    borderBottom: '1px solid #e8ecef'
  },
  summaryRow: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: '8px'
  },
  summaryLabel: {
    fontSize: '12px',
    color: '#7f8c8d',
    width: '60px',
    flexShrink: 0
  },
  summaryValue: {
    fontSize: '14px',
    color: '#2c3e50',
    fontWeight: '500'
  },
  summaryStats: {
    display: 'flex',
    gap: '12px',
    marginTop: '12px'
  },
  statBox: {
    flex: 1,
    backgroundColor: '#fff',
    padding: '12px',
    borderRadius: '8px',
    textAlign: 'center',
    border: '1px solid #e8ecef'
  },
  statLabel: {
    display: 'block',
    fontSize: '11px',
    color: '#7f8c8d',
    marginBottom: '4px'
  },
  statValue: {
    fontSize: '18px',
    fontWeight: '700',
    color: '#2c3e50'
  },
  content: {
    flex: 1,
    overflow: 'auto',
    padding: '16px 24px'
  },
  loading: {
    padding: '40px',
    textAlign: 'center',
    color: '#7f8c8d'
  },
  empty: {
    padding: '40px',
    textAlign: 'center',
    color: '#95a5a6'
  },
  emptyIcon: {
    fontSize: '48px',
    display: 'block',
    marginBottom: '12px'
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: '13px'
  },
  th: {
    padding: '10px 8px',
    textAlign: 'left',
    fontWeight: '600',
    color: '#7f8c8d',
    borderBottom: '2px solid #e8ecef',
    whiteSpace: 'nowrap'
  },
  tr: {
    borderBottom: '1px solid #f0f3f5'
  },
  td: {
    padding: '12px 8px',
    color: '#2c3e50'
  },
  recordId: {
    backgroundColor: '#e3f2fd',
    color: '#1976d2',
    padding: '2px 8px',
    borderRadius: '4px',
    fontSize: '12px',
    fontWeight: '600'
  },
  totalRow: {
    backgroundColor: '#f8f9fa'
  },
  totalTd: {
    padding: '12px 8px',
    fontWeight: '600',
    color: '#2c3e50',
    borderTop: '2px solid #e8ecef'
  },
  footer: {
    padding: '16px 24px',
    borderTop: '1px solid #e8ecef',
    display: 'flex',
    justifyContent: 'flex-end'
  },
  closeButton: {
    padding: '10px 24px',
    backgroundColor: '#6c757d',
    color: '#fff',
    border: 'none',
    borderRadius: '6px',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer'
  }
};

// CSS 애니메이션 추가
const styleSheet = document.createElement('style');
styleSheet.textContent = `
  @keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }
  @keyframes slideUp {
    from { transform: translateY(20px); opacity: 0; }
    to { transform: translateY(0); opacity: 1; }
  }
`;
if (!document.querySelector('#material-outgoing-modal-styles')) {
  styleSheet.id = 'material-outgoing-modal-styles';
  document.head.appendChild(styleSheet);
}

export default MaterialOutgoingModal;
